import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Building2, Users, Plus, Settings, Shield, UserMinus, Loader2, Check, Crown, User, Eye, DollarSign, AlertTriangle, PauseCircle, PlayCircle, Palette } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useToast } from '@/components/ui/toast'
import { useAuth } from '@/context/AuthContext'
import { api } from '@/api/client'
import type { PropertyDisposition } from '@/api/client'
import { apiErrorMessage } from '@/lib/http-status'
import { usePageTitle } from '@/lib/usePageTitle'

type AgencyMemberUser = {
  id?: string
  name?: string
  email?: string
  photo?: string
}

type AgencyMember = {
  id: string
  user_id?: string
  user?: AgencyMemberUser
  role: string
  status?: string
  pause_reason?: string | null
}

/** Roles the Members UI can assign (matches tenant-authorization normalizeAgencyMembershipInput). */
export const ASSIGNABLE_ROLES = ['admin', 'member', 'guest'] as const
export type AssignableRole = (typeof ASSIGNABLE_ROLES)[number]
export type AffiliationMode = 'exclusive' | 'non_exclusive'

/**
 * Server rules (tenant-authorization.js:138-143): admin→exclusive, guest→non_exclusive,
 * member→either. Used by invite POST and role-change PUT so the client never omits a
 * mode the server will reject (D-17 / Step 0).
 */
export function resolveAffiliationMode(
  role: string,
  memberChoice: AffiliationMode = 'non_exclusive',
): AffiliationMode {
  if (role === 'admin') return 'exclusive'
  if (role === 'guest') return 'non_exclusive'
  return memberChoice
}

/** The live case the tied-listings endpoint reports for a listing, if any (F-9). */
type AgencyDispositionCaseSummary = {
  id: string
  status: 'pending' | 'agreed' | 'disputed'
  agency_proposed_disposition: PropertyDisposition | null
  agent_proposed_disposition: PropertyDisposition | null
}

type AgencyTiedListing = {
  id: string
  title: string
  disposition_case?: AgencyDispositionCaseSummary | null
}

type AgencyWithMembers = {
  id: string
  name: string
  license_number?: string | null
  description?: string | null
  phone?: string | null
  email?: string | null
  address?: string | null
  myRole?: string
  members?: AgencyMember[]
  listings?: Array<{ id: string }>
}

/**
 * Stored agency-membership roles (tenant_memberships / agency_members).
 * Label "Member" is the agency-side profile (`agency:<id>`); the personal
 * workspace (`personal:<userId>`) is the separate agent profile — do not
 * conflate the two. Inbound `agent` at tenant-authorization.js:131 is only a
 * legacy alias normalised into this vocabulary.
 */
export const ROLE_META: Record<string, { label: string; icon: LucideIcon; color: string; description: string }> = {
  owner: { label: 'Owner', icon: Crown, color: 'bg-amber-100 text-amber-700 border-amber-200', description: 'Full control over agency — transfer only' },
  admin: { label: 'Admin', icon: Shield, color: 'bg-red-100 text-red-700 border-red-200', description: 'Manage members and settings (exclusive affiliation)' },
  member: { label: 'Member', icon: User, color: 'bg-green-100 text-green-700 border-green-200', description: 'Agency membership — list properties and manage leads' },
  guest: { label: 'Guest', icon: Eye, color: 'bg-gray-100 text-gray-700 border-gray-200', description: 'Limited access (non-exclusive affiliation)' },
}

const UNKNOWN_ROLE_STYLE = 'bg-gray-100 text-gray-700 border-gray-200'

/** Badge/select label for a role; unknown roles show the raw value (no silent mislabel). */
export function roleLabel(role: string): string {
  return ROLE_META[role]?.label ?? role
}

export function roleBadgeClass(role: string): string {
  return ROLE_META[role]?.color ?? UNKNOWN_ROLE_STYLE
}

export function AgencyManagementPage() {
  const { agent, loading: authLoading } = useAuth()
  const { addToast } = useToast()
  usePageTitle('Agency Management')
  const [loading, setLoading] = useState(true)
  const [agency, setAgency] = useState<AgencyWithMembers | null>(null)
  const [activeTab, setActiveTab] = useState('overview')

  // Create agency form
  const [createMode, setCreateMode] = useState(false)
  const [createForm, setCreateForm] = useState({ name: '', license_number: '', description: '', phone: '', email: '', address: '', site_hosting_type: 'none' })
  const [creating, setCreating] = useState(false)

  // Invite form
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<AssignableRole>('member')
  const [inviteAffiliation, setInviteAffiliation] = useState<AffiliationMode>('non_exclusive')
  const [inviting, setInviting] = useState(false)
  const [departure, setDeparture] = useState<null | {
    memberId: string
    userId: string
    name: string
    listings: AgencyTiedListing[]
    assignments: Record<string, string>
    proposals: Record<string, PropertyDisposition | ''>
    opening: string
    saving: boolean
    error: string
  }>(null)
  const [pauseTarget, setPauseTarget] = useState<null | { memberId: string; name: string }>(null)
  const [pauseReason, setPauseReason] = useState('')
  const [pauseSaving, setPauseSaving] = useState(false)
  const [resumingId, setResumingId] = useState('')

  const loadAgency = useCallback(() => {
    // Do not setLoading(true) on refresh — that unmounts the Members tab (and
    // invite form) mid-interaction whenever invite/role/pause reloads the agency.
    // Initial mount already starts with loading=true.
    api.getMyAgency()
      .then(data => {
        setAgency(data as AgencyWithMembers)
        setLoading(false)
      })
      .catch((err: unknown) => {
        setLoading(false)
        addToast({ title: 'Failed to load agency', description: apiErrorMessage(err, 'Could not load agency data'), variant: 'error' })
      })
  }, [addToast])

  useEffect(() => {
    if (!agent) return
    loadAgency()
  }, [agent, loadAgency])

  const handleCreate = async () => {
    if (!createForm.name.trim()) return
    setCreating(true)
    try {
      const data = await api.createAgency(createForm) as AgencyWithMembers
      setAgency(data)
      setCreateMode(false)
      addToast({ title: 'Agency created', description: `${data.name} is ready.`, variant: 'success' })
    } catch (e: unknown) {
      addToast({ title: 'Failed to create agency', description: apiErrorMessage(e, 'Could not create agency'), variant: 'error' })
    } finally {
      setCreating(false)
    }
  }

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !agency) return
    setInviting(true)
    const affiliation_mode = resolveAffiliationMode(inviteRole, inviteAffiliation)
    try {
      await api.inviteMember(agency.id, { email: inviteEmail, role: inviteRole, affiliation_mode })
      setInviteEmail('')
      loadAgency()
      addToast({ title: 'Invitation sent', description: `Invite sent to ${inviteEmail}.`, variant: 'success' })
    } catch (e: unknown) {
      addToast({ title: 'Failed to invite', description: apiErrorMessage(e, 'Could not send invitation'), variant: 'error' })
    } finally {
      setInviting(false)
    }
  }

  const openDeparture = async (memberId: string, memberUserId?: string, memberName?: string) => {
    if (!memberUserId || !agency) return
    try {
      const data = await api.getTiedListings(agency.id, memberId) as { listings?: AgencyTiedListing[] }
      const listings = data.listings || []
      const assignments: Record<string, string> = {}
      const proposals: Record<string, PropertyDisposition | ''> = {}
      listings.forEach((l: AgencyTiedListing) => {
        assignments[l.id] = ''
        proposals[l.id] = ''
      })
      setDeparture({
        memberId,
        userId: memberUserId,
        name: memberName || 'Agent',
        listings,
        assignments,
        proposals,
        opening: '',
        saving: false,
        error: '',
      })
    } catch (e: unknown) {
      addToast({ title: 'Failed to load tied listings', description: apiErrorMessage(e, 'Could not load listings'), variant: 'error' })
    }
  }

  const completeDeparture = async () => {
    if (!agency || !departure) return
    if (departure.listings.length > 0) {
      // A listing whose custody is with an open disposition case is not settled yet,
      // and the backend gate would refuse the departure — say so here instead (F-9).
      const openCase = departure.listings.find((l) => l.disposition_case)
      if (openCase) {
        setDeparture((d) => d ? {
          ...d,
          error: `A disposition case is still open for “${openCase.title}”. It has to be resolved before the affiliation can end.`,
        } : d)
        return
      }
      const missing = departure.listings.filter((l) => !departure.assignments[l.id])
      if (missing.length) {
        setDeparture((d) => d ? { ...d, error: 'Assign every agency-tied listing to another agent first.' } : d)
        return
      }
    }
    setDeparture((d) => d ? { ...d, saving: true, error: '' } : d)
    try {
      for (const listing of departure.listings) {
        await api.reassignAgencyListing(agency.id, listing.id, {
          from_agent_id: departure.userId,
          to_agent_id: departure.assignments[listing.id],
        })
      }
      await api.endAgencyMembership(agency.id, departure.memberId, { reason: 'departure' })
      setDeparture(null)
      loadAgency()
    } catch (e: unknown) {
      setDeparture((d) => d ? { ...d, saving: false, error: apiErrorMessage(e, 'Failed') } : d)
    }
  }

  // F-9 — settle a listing by agreement instead of handing it to a colleague. Either
  // party may open the case; this is the agency's side of it.
  const openDispositionCase = async (listing: AgencyTiedListing) => {
    if (!agency || !departure) return
    const disposition = departure.proposals[listing.id]
    if (!disposition) {
      setDeparture((d) => d ? { ...d, error: 'Choose what should happen to the listing before opening a case.' } : d)
      return
    }
    setDeparture((d) => d ? { ...d, opening: listing.id, error: '' } : d)
    try {
      await api.createPropertyDispositionCase(listing.id, { disposition })
      const data = await api.getTiedListings(agency.id, departure.memberId) as { listings?: AgencyTiedListing[] }
      setDeparture((d) => d ? { ...d, listings: data.listings || [], opening: '' } : d)
      addToast({
        title: 'Disposition case opened',
        description: `${departure.name} can now record their own decision for “${listing.title}”.`,
        variant: 'success',
      })
    } catch (e: unknown) {
      setDeparture((d) => d ? {
        ...d,
        opening: '',
        error: apiErrorMessage(e, 'Could not open a disposition case'),
      } : d)
    }
  }

  const handleUpdateRole = async (memberId: string, newRole: string) => {
    if (!agency) return
    // admin/guest force affiliation_mode (Step 0: PUT role=admin alone 400s on non_exclusive).
    // member omits affiliation_mode so the server keeps the current mode (updateAgencyMembership:451).
    const payload: Record<string, string> = { role: newRole }
    if (newRole === 'admin' || newRole === 'guest') {
      payload.affiliation_mode = resolveAffiliationMode(newRole)
    }
    try {
      await api.updateMember(agency.id, memberId, payload)
      loadAgency()
    } catch (e: unknown) {
      addToast({ title: 'Could not update role', description: apiErrorMessage(e, 'Please try again'), variant: 'error' })
    }
  }

  const submitPause = async () => {
    if (!agency || !pauseTarget) return
    setPauseSaving(true)
    try {
      await api.pauseMember(agency.id, pauseTarget.memberId, pauseReason.trim())
      addToast({ title: 'Member paused', description: `${pauseTarget.name} is temporarily suspended.`, variant: 'success' })
      setPauseTarget(null)
      setPauseReason('')
      loadAgency()
    } catch (e) {
      addToast({ title: 'Could not pause member', description: apiErrorMessage(e, 'Please try again'), variant: 'error' })
    } finally {
      setPauseSaving(false)
    }
  }

  const handleResume = async (memberId: string, name?: string) => {
    if (!agency) return
    setResumingId(memberId)
    try {
      await api.resumeMember(agency.id, memberId)
      addToast({ title: 'Member resumed', description: `${name || 'The member'} has full access again.`, variant: 'success' })
      loadAgency()
    } catch (e) {
      addToast({ title: 'Could not resume member', description: apiErrorMessage(e, 'Please try again'), variant: 'error' })
    } finally {
      setResumingId('')
    }
  }

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!agent) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold">Please sign in</h2>
          <p className="text-muted-foreground">You need to be logged in to manage your agency</p>
          <Link to="/login"><Button className="mt-4">Sign In</Button></Link>
        </div>
      </div>
    )
  }

  // Not in an agency yet
  if (!loading && !agency && !createMode) {
    return (
      <div className="min-h-screen bg-[var(--lc-bg-page)] px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <Building2 className="mx-auto h-16 w-16 text-primary/60" />
          <h1 className="mt-6 text-3xl font-bold">Create Your Agency</h1>
          <p className="mt-2 text-muted-foreground">Set up your agency on REB to unlock white-label websites, team management, and multi-channel distribution.</p>
          <Button className="mt-6 gap-2" onClick={() => setCreateMode(true)}><Plus className="h-4 w-4" />Create Agency</Button>
        </div>
      </div>
    )
  }

  if (createMode) {
    return (
      <div className="min-h-screen bg-[var(--lc-bg-page)] px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-xl">
          <Card>
            <CardHeader>
              <CardTitle>Create Agency</CardTitle>
              <CardDescription>Register your real estate agency on REB</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Agency Name *</Label><Input value={createForm.name} onChange={e => setCreateForm({ ...createForm, name: e.target.value })} placeholder="e.g., Haddad Premium Properties" /></div>
              <div><Label>License Number</Label><Input value={createForm.license_number} onChange={e => setCreateForm({ ...createForm, license_number: e.target.value })} placeholder="e.g., AL-8892" /></div>
              <div><Label>Description</Label><textarea value={createForm.description} onChange={e => setCreateForm({ ...createForm, description: e.target.value })} rows={3} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Describe your agency..." /></div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label>Phone</Label><Input value={createForm.phone} onChange={e => setCreateForm({ ...createForm, phone: e.target.value })} /></div>
                <div><Label>Email</Label><Input value={createForm.email} onChange={e => setCreateForm({ ...createForm, email: e.target.value })} /></div>
              </div>
              <div><Label>Address</Label><Input value={createForm.address} onChange={e => setCreateForm({ ...createForm, address: e.target.value })} placeholder="Office address" /></div>
              <div>
                <Label>Site hosting (Decision 4)</Label>
                <select
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm h-10"
                  value={createForm.site_hosting_type || 'none'}
                  onChange={e => setCreateForm({ ...createForm, site_hosting_type: e.target.value })}
                >
                  <option value="none">Marketplace-only (no public agency site)</option>
                  <option value="external">External website + selective marketplace syndication</option>
                  <option value="whitelabel">Platform white-label site (custom domain eligible)</option>
                </select>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setCreateMode(false)}>Cancel</Button>
                <Button onClick={handleCreate} disabled={creating || !createForm.name.trim()} className="gap-2">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}Create Agency
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!agency) {
    return null
  }

  const myRole = agency.myRole || 'agent'
  const canManageMembers = ['owner', 'admin'].includes(myRole)
  const canManageSettings = ['owner', 'admin'].includes(myRole)
  const canManagePublicProfile = ['owner', 'admin', 'marketer'].includes(myRole)

  return (
    <div className="min-h-screen bg-[var(--lc-bg-page)]">
      {/* Header */}
      <div className="border-b bg-[var(--lc-surface)] px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary-faint p-3">
                <Building2 className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">{agency.name}</h1>
                <p className="text-sm text-muted-foreground">License {agency.license_number} &bull; {agency.members?.length || 1} member{(agency.members?.length || 1) > 1 ? 's' : ''}</p>
              </div>
            </div>
            <div className="flex gap-2">
              {canManagePublicProfile && <Link to="/agency/public-profile"><Button variant="outline" className="gap-2"><Eye className="h-4 w-4" />Public Profile</Button></Link>}
              <Link to="/agency/pricing"><Button variant="outline" className="gap-2"><DollarSign className="h-4 w-4" />Price Health</Button></Link>
              <Link to="/white-label"><Button variant="outline" className="gap-2"><Settings className="h-4 w-4" />White-Label Sites</Button></Link>
              <Link to="/integrations"><Button variant="outline" className="gap-2"><Settings className="h-4 w-4" />Integrations</Button></Link>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="members">Members</TabsTrigger>
            {canManageSettings && <TabsTrigger value="settings">Settings</TabsTrigger>}
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview">
            <div className="grid gap-6 lg:grid-cols-3">
              <Card className="lg:col-span-2">
                <CardHeader><CardTitle>Agency Details</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div><Label className="text-sm text-muted-foreground">Name</Label><p className="font-medium">{agency.name}</p></div>
                    <div><Label className="text-sm text-muted-foreground">License</Label><p className="font-medium">{agency.license_number || '—'}</p></div>
                    <div><Label className="text-sm text-muted-foreground">Phone</Label><p className="font-medium">{agency.phone || '—'}</p></div>
                    <div><Label className="text-sm text-muted-foreground">Email</Label><p className="font-medium">{agency.email || '—'}</p></div>
                  </div>
                  <div><Label className="text-sm text-muted-foreground">Address</Label><p className="font-medium">{agency.address || '—'}</p></div>
                  <div><Label className="text-sm text-muted-foreground">Description</Label><p className="text-sm text-muted-foreground">{agency.description || 'No description'}</p></div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle>Quick Stats</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-primary" />
                    <div><p className="font-bold">{agency.members?.length || 1}</p><p className="text-xs text-muted-foreground">Team Members</p></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 text-primary" />
                    <div><p className="font-bold">{agency.listings?.length || 0}</p><p className="text-xs text-muted-foreground">Listings</p></div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* MEMBERS */}
          <TabsContent value="members">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" />Team Members</CardTitle>
                <CardDescription>Manage roles and permissions for your team</CardDescription>
              </CardHeader>
              <CardContent>
                {canManageMembers && (
                  <div className="mb-6 space-y-3">
                    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
                      <Input
                        placeholder="Enter email to invite"
                        value={inviteEmail}
                        onChange={e => setInviteEmail(e.target.value)}
                        className="sm:w-72"
                        aria-label="Invite email"
                      />
                      <div>
                        <Label htmlFor="invite-role" className="sr-only">Invite role</Label>
                        <select
                          id="invite-role"
                          value={inviteRole}
                          onChange={e => setInviteRole(e.target.value as AssignableRole)}
                          className="rounded-md border border-input bg-background px-3 py-2 text-sm h-10"
                          data-testid="invite-role"
                        >
                          {ASSIGNABLE_ROLES.map((key) => (
                            <option key={key} value={key}>{ROLE_META[key].label}</option>
                          ))}
                        </select>
                      </div>
                      {inviteRole === 'member' ? (
                        <div>
                          <Label htmlFor="invite-affiliation" className="sr-only">Invite affiliation</Label>
                          <select
                            id="invite-affiliation"
                            value={inviteAffiliation}
                            onChange={e => setInviteAffiliation(e.target.value as AffiliationMode)}
                            className="rounded-md border border-input bg-background px-3 py-2 text-sm h-10"
                            data-testid="invite-affiliation"
                          >
                            <option value="non_exclusive">Non-exclusive</option>
                            <option value="exclusive">Exclusive</option>
                          </select>
                        </div>
                      ) : null}
                      <Button onClick={handleInvite} disabled={inviting || !inviteEmail.trim()} className="gap-2">
                        {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Invite
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground max-w-2xl">
                      {inviteRole === 'admin'
                        ? 'Admin requires exclusive affiliation. Their existing listings transfer to the agency.'
                        : inviteRole === 'guest'
                          ? 'Guest requires non-exclusive affiliation — they keep personal inventory.'
                          : resolveAffiliationMode('member', inviteAffiliation) === 'exclusive'
                            ? 'Exclusive: their existing listings transfer to the agency (personal workspace becomes read-only).'
                            : 'Non-exclusive: they keep a live personal workspace; agency inventory stays separate.'}
                    </p>
                  </div>
                )}

                <div className="space-y-3">
                  {agency.members?.map((member: AgencyMember) => {
                    const Icon = ROLE_META[member.role]?.icon ?? User
                    const isMe = member.user_id === agent.id
                    const isPaused = member.status === 'paused'
                    const isOwnerRow = member.role === 'owner'
                    const showRoleSelect = canManageMembers && !isMe && !isOwnerRow
                    return (
                      <div key={member.id} className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={member.user?.photo} />
                          <AvatarFallback>{member.user?.name?.split(' ').map((n) => n[0]).join('')}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-medium">{member.user?.name || 'Unknown'}</p>
                            {isMe && <Badge variant="outline" className="text-xs">You</Badge>}
                            {isPaused && <Badge className="border-amber-200 bg-amber-100 text-amber-800 text-xs">Paused</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground">{member.user?.email}</p>
                          {isPaused && member.pause_reason && (
                            <p className="mt-1 text-xs text-amber-700">Paused: {member.pause_reason}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {showRoleSelect ? (
                            <select
                              value={member.role}
                              onChange={e => handleUpdateRole(member.id, e.target.value)}
                              className="rounded-md border border-input bg-background px-2 py-1 text-sm"
                              disabled={isPaused}
                              aria-label={`Role for ${member.user?.name || 'member'}`}
                            >
                              {ASSIGNABLE_ROLES.map((key) => (
                                <option key={key} value={key}>{ROLE_META[key].label}</option>
                              ))}
                            </select>
                          ) : (
                            <Badge className={roleBadgeClass(member.role)}>
                              <Icon className="mr-1 h-3 w-3" aria-hidden />
                              {roleLabel(member.role)}
                            </Badge>
                          )}
                          {canManageMembers && !isMe && !isOwnerRow && (
                            isPaused ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-1"
                                disabled={resumingId === member.id}
                                onClick={() => handleResume(member.id, member.user?.name)}
                              >
                                {resumingId === member.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
                                Resume
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="gap-1"
                                aria-label={`Pause ${member.user?.name || 'member'}`}
                                onClick={() => { setPauseTarget({ memberId: member.id, name: member.user?.name || 'this member' }); setPauseReason('') }}
                              >
                                <PauseCircle className="h-4 w-4" />
                              </Button>
                            )
                          )}
                          {canManageMembers && !isMe && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive"
                              aria-label={`End affiliation for ${member.user?.name || 'member'}`}
                              onClick={() => openDeparture(member.id, member.user_id || member.user?.id, member.user?.name)}
                            >
                              <UserMinus className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>

                <div className="mt-6 rounded-lg bg-[var(--lc-surface-sunken)] p-4">
                  <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><Shield className="h-4 w-4" />Role Permissions</h4>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {Object.entries(ROLE_META).map(([key, meta]) => (
                      <div key={key} className="flex items-start gap-2 text-sm">
                        <Badge className={`${meta.color} text-xs shrink-0 mt-0.5`}>{meta.label}</Badge>
                        <span className="text-muted-foreground">{meta.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SETTINGS */}
          {canManageSettings && (
            <TabsContent value="settings">
              <Card>
                <CardHeader><CardTitle>Agency Settings</CardTitle><CardDescription>Manage agency-wide identity and controls.</CardDescription></CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-4 rounded-lg border bg-[var(--lc-surface-sunken)] p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-3">
                      <Palette className="mt-1 h-5 w-5 text-primary" aria-hidden="true" />
                      <div>
                        <h3 className="font-semibold">Identity and branding</h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                          Agency name, description, logo, favicon, brand colors, and font.
                        </p>
                      </div>
                    </div>
                    <Button asChild variant="outline">
                      <Link to="/agency/settings/branding">Manage brand</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {departure && (
        <div className="fixed inset-0 z-overlay flex items-center justify-center lc-overlay p-4">
          <div className="w-full max-w-lg rounded-xl bg-[var(--lc-surface)] p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold">End affiliation — {departure.name}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Every agency-tied listing has to be settled first — reassigned to another
              active member, or decided with {departure.name} in a disposition case.
            </p>
            {departure.listings.length === 0 ? (
              <p className="mt-4 text-sm">No agency-tied listings. You can end affiliation now.</p>
            ) : (
              <div className="mt-4 space-y-3">
                {departure.listings.map((listing) => (
                  <div key={listing.id} className="rounded-lg border p-3">
                    <p className="text-sm font-medium">{listing.title}</p>
                    {listing.disposition_case ? (
                      <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                        <p>
                          Disposition case{' '}
                          <span className="font-medium text-foreground">{listing.disposition_case.status}</span>
                          {listing.disposition_case.status === 'disputed'
                            ? ' — the two sides disagree.'
                            : listing.disposition_case.status === 'agreed'
                              ? ' — both sides agree and it can be resolved.'
                              : ` — waiting on ${listing.disposition_case.agent_proposed_disposition ? 'the agency' : departure.name}.`}
                        </p>
                        <Button asChild size="sm" variant="outline">
                          <Link to={`/listings/${listing.id}/disposition`}>Open the case</Link>
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Label className="mt-2 text-xs" htmlFor={`reassign-${listing.id}`}>Reassign to</Label>
                        <select
                          id={`reassign-${listing.id}`}
                          className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm h-10"
                          value={departure.assignments[listing.id] || ''}
                          onChange={(e) =>
                            setDeparture((d) =>
                              d
                                ? {
                                    ...d,
                                    assignments: { ...d.assignments, [listing.id]: e.target.value },
                                  }
                                : d,
                            )
                          }
                        >
                          <option value="">Select agent</option>
                          {(agency.members || [])
                            .filter((m: AgencyMember) => m.status !== 'ended' && (m.user_id || m.user?.id) !== departure.userId)
                            .map((m: AgencyMember) => (
                              <option key={m.id} value={m.user_id || m.user?.id}>
                                {m.user?.name || m.user_id}
                              </option>
                            ))}
                        </select>
                        <div className="mt-3 border-t pt-3">
                          <Label className="text-xs" htmlFor={`disposition-${listing.id}`}>
                            Or decide it with {departure.name}
                          </Label>
                          <div className="mt-1 flex gap-2">
                            <select
                              id={`disposition-${listing.id}`}
                              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm h-10"
                              value={departure.proposals[listing.id] || ''}
                              onChange={(e) =>
                                setDeparture((d) =>
                                  d
                                    ? {
                                        ...d,
                                        proposals: {
                                          ...d.proposals,
                                          [listing.id]: e.target.value as PropertyDisposition | '',
                                        },
                                      }
                                    : d,
                                )
                              }
                            >
                              <option value="">Propose an outcome</option>
                              <option value="agency_retains">The agency keeps it</option>
                              <option value="agent_retains">{departure.name} keeps it</option>
                              <option value="archive">Archive it</option>
                            </select>
                            <Button
                              variant="outline"
                              className="shrink-0 gap-2"
                              disabled={departure.opening === listing.id}
                              onClick={() => openDispositionCase(listing)}
                            >
                              {departure.opening === listing.id && <Loader2 className="h-4 w-4 animate-spin" />}
                              Open case
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
            {departure.error && <p className="mt-3 text-sm text-destructive">{departure.error}</p>}
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeparture(null)} disabled={departure.saving}>Cancel</Button>
              <Button onClick={completeDeparture} disabled={departure.saving} className="gap-2">
                {departure.saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Reassign & end affiliation
              </Button>
            </div>
          </div>
        </div>
      )}

      {pauseTarget && (
        <div className="fixed inset-0 z-overlay flex items-center justify-center lc-overlay p-4" role="dialog" aria-modal="true" aria-label={`Pause ${pauseTarget.name}`}>
          <div className="w-full max-w-md rounded-xl bg-[var(--lc-surface)] p-6 shadow-xl">
            <h3 className="text-lg font-bold">Pause {pauseTarget.name}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Paused members keep their account but lose agency access and stop receiving routed leads until you resume them.
            </p>
            <div className="mt-4">
              <Label htmlFor="pause-reason">Reason</Label>
              <textarea
                id="pause-reason"
                rows={3}
                value={pauseReason}
                onChange={(e) => setPauseReason(e.target.value)}
                placeholder="e.g. On leave until August, under review"
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
              <p className="mt-1 text-xs text-muted-foreground">Shown on the member’s row and recorded in the audit log.</p>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setPauseTarget(null); setPauseReason('') }} disabled={pauseSaving}>Cancel</Button>
              <Button onClick={submitPause} disabled={pauseSaving || pauseReason.trim().length < 3} className="gap-2">
                {pauseSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                Pause member
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
